import type { Frame } from "../renderer/Frame";
import type { DPoint, DrawingObject } from "./types";
import { channelOffset, rayEnd, XY, FIB_LEVELS, FIB_EXT_LEVELS } from "./geometry";
import { regression, anchoredVwap, tickStats, zoneFill, idxOf } from "./analytics";
import { formatDuration, formatFullTime } from "../core/TimeScale";

export interface Label { x: number; y: number; text: string; color?: string; bg?: string | true; align?: CanvasTextAlign; size?: number; base?: CanvasTextBaseline }
export interface Outline { lines: XY[][]; fills: XY[][]; labels: Label[]; dashed?: XY[][]; wide?: boolean }

const FAR = 6000;
const ext = (a: XY, b: XY): XY => { const dx = b.x - a.x, dy = b.y - a.y, k = FAR / (Math.hypot(dx, dy) || 1); return { x: a.x + dx * k, y: a.y + dy * k }; };
const lerp = (a: XY, b: XY, t: number): XY => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
const mid = (a: XY, b: XY) => lerp(a, b, 0.5);
export const fmt = (v: number, dec: number) => v.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
const pct = (a: number, b: number) => `${((b - a) / a * 100 >= 0 ? "+" : "")}${((b - a) / a * 100).toFixed(2)}%`;

export function ellipsePts(cx: number, cy: number, rx: number, ry: number, from = 0, to = Math.PI * 2, n = 48): XY[] {
  const out: XY[] = [];
  for (let k = 0; k <= n; k++) { const t = from + (to - from) * (k / n); out.push({ x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) }); }
  return out;
}
export function bezierQ(a: XY, c: XY, b: XY, n = 32): XY[] {
  const out: XY[] = [];
  for (let k = 0; k <= n; k++) { const t = k / n, u = 1 - t; out.push({ x: u * u * a.x + 2 * u * t * c.x + t * t * b.x, y: u * u * a.y + 2 * u * t * c.y + t * t * b.y }); }
  return out;
}
export function bezierC(a: XY, c1: XY, c2: XY, b: XY, n = 40): XY[] {
  const out: XY[] = [];
  for (let k = 0; k <= n; k++) { const t = k / n, u = 1 - t; out.push({ x: u ** 3 * a.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t ** 3 * b.x, y: u ** 3 * a.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t ** 3 * b.y }); }
  return out;
}
// Arc through a, b with bulge control c → circle through the three points (falls back to a quadratic curve if collinear).
function arcThrough(a: XY, b: XY, c: XY): XY[] {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-6) return bezierQ(a, c, b);
  const ux = ((a.x ** 2 + a.y ** 2) * (b.y - c.y) + (b.x ** 2 + b.y ** 2) * (c.y - a.y) + (c.x ** 2 + c.y ** 2) * (a.y - b.y)) / d;
  const uy = ((a.x ** 2 + a.y ** 2) * (c.x - b.x) + (b.x ** 2 + b.y ** 2) * (a.x - c.x) + (c.x ** 2 + c.y ** 2) * (b.x - a.x)) / d;
  const r = Math.hypot(a.x - ux, a.y - uy);
  let t0 = Math.atan2(a.y - uy, a.x - ux), t1 = Math.atan2(b.y - uy, b.x - ux), tc = Math.atan2(c.y - uy, c.x - ux);
  const norm = (t: number) => ((t % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  let sweep = norm(t1 - t0), cs = norm(tc - t0);
  if (cs > sweep) { sweep -= 2 * Math.PI; }
  return ellipsePts(ux, uy, r, r, t0, t0 + sweep, 48);
}

const FIB_TIME = [0, 1, 2, 3, 5, 8, 13, 21, 34, 55];
const GANN = [0.25, 0.382, 0.5, 0.618, 0.75];
const FAN_RATIOS: [string, number][] = [["8/1", 8], ["4/1", 4], ["3/1", 3], ["2/1", 2], ["1/1", 1], ["1/2", 1 / 2], ["1/3", 1 / 3], ["1/4", 1 / 4], ["1/8", 1 / 8]];

const seq = (pts: XY[]): XY[][] => (pts.length > 1 ? [pts] : []);
const fanLabels = (labels: string[], pts: XY[], color?: string): Label[] => pts.map((p, i) => ({ x: p.x, y: p.y - 12, text: labels[i] ?? "", align: "center" as CanvasTextAlign, color, size: 11 }));
const wavePts = (pts: XY[], names: string[]): Label[] => pts.map((p, i) => ({ x: p.x, y: i % 2 ? p.y + 12 : p.y - 12, text: names[i] ?? String(i), align: "center" as CanvasTextAlign, size: 11 }));

function pitchfork(f: Frame, kind: DrawingObject["type"], a: XY, b: XY, c: XY): XY[][] {
  const m = mid(b, c);
  const origin = kind === "schiff" ? { x: a.x, y: (a.y + b.y) / 2 } : kind === "mschiff" ? mid(a, b) : a;
  const dir = { x: m.x - origin.x, y: m.y - origin.y };
  const far = (p: XY) => ext(p, { x: p.x + dir.x, y: p.y + dir.y });
  if (kind === "pitchfan") return [[origin, ext(origin, b)], [origin, ext(origin, m)], [origin, ext(origin, c)], [b, c]];
  const [u, v] = kind === "insidefork" ? [mid(b, m), mid(c, m)] : [b, c];
  return [[origin, far(m)], [u, far(u)], [v, far(v)], [b, c]];
}

// Screen-space outline (polylines, filled polygons, labels) for every drawing type that is essentially line art.
export function outline(f: Frame, d: DrawingObject, pts: XY[]): Outline {
  const o: Outline = { lines: [], fills: [], labels: [] };
  const { L } = f; const dec = f.opts.decimals; const [a, b, c, e] = pts; const P = d.points;
  const done = (n: number) => pts.length >= n;
  switch (d.type) {
    case "trend": case "polyline": case "path": case "brush": case "highlighter": o.lines = seq(pts); break;
    case "ray": if (done(2)) o.lines = [[a, rayEnd(f, a, b)]]; break;
    case "extline": if (done(2)) o.lines = [[ext(b, a), ext(a, b)]]; break;
    case "infoline": {
      if (!done(2)) break;
      o.lines = [[a, b]];
      const bars = Math.round(Math.abs(f.fracIndexAt(b.x) - f.fracIndexAt(a.x)));
      const ang = (-Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
      o.labels.push({ x: mid(a, b).x, y: mid(a, b).y - 14, text: `${P[1].price - P[0].price >= 0 ? "+" : ""}${fmt(P[1].price - P[0].price, dec)} (${pct(P[0].price, P[1].price)}) · ${bars} bars · ${formatDuration(P[1].time - P[0].time)} · ${ang.toFixed(1)}°`, align: "center", bg: true });
      break;
    }
    case "trendangle": {
      if (!done(2)) break;
      const ang = (-Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
      o.lines = [[a, b]]; o.dashed = [[a, { x: a.x + 60 * Math.sign(b.x - a.x || 1), y: a.y }]];
      o.lines.push(ellipsePts(a.x, a.y, 28, 28, b.x >= a.x ? 0 : Math.PI, Math.atan2(b.y - a.y, b.x - a.x), 16));
      o.labels.push({ x: a.x + 34 * Math.sign(b.x - a.x || 1), y: a.y - 14, text: `${ang.toFixed(1)}°`, align: "center" });
      break;
    }
    case "hline": o.lines = [[{ x: L.plotLeft, y: a.y }, { x: L.plotRight, y: a.y }]]; o.labels.push({ x: L.plotRight + 4, y: a.y, text: fmt(P[0].price, dec), bg: d.style.color, color: "#0B0E14" }); break;
    case "hray": o.lines = [[a, { x: L.plotRight, y: a.y }]]; o.labels.push({ x: L.plotRight + 4, y: a.y, text: fmt(P[0].price, dec), bg: d.style.color, color: "#0B0E14" }); break;
    case "vline": o.lines = [[{ x: a.x, y: L.plotTop }, { x: a.x, y: L.priceBottom }]]; break;
    case "crossline": o.lines = [[{ x: L.plotLeft, y: a.y }, { x: L.plotRight, y: a.y }], [{ x: a.x, y: L.plotTop }, { x: a.x, y: L.priceBottom }]]; o.labels.push({ x: L.plotRight + 4, y: a.y, text: fmt(P[0].price, dec), bg: d.style.color, color: "#0B0E14" }); break;
    case "channel": {
      if (!done(2)) break;
      o.lines = [[a, b]];
      if (!done(3)) break;
      const off = channelOffset(a, b, c), a2 = { x: a.x + off.x, y: a.y + off.y }, b2 = { x: b.x + off.x, y: b.y + off.y };
      o.lines.push([a2, b2]); o.fills = [[a, b, b2, a2]]; o.dashed = [[mid(a, a2), mid(b, b2)]];
      break;
    }
    case "flatchannel": {
      if (!done(2)) break;
      o.lines = [[a, b]];
      if (!done(3)) break;
      const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x);
      const fa = a.x <= b.x ? a : b, fb = a.x <= b.x ? b : a;
      o.lines.push([{ x: x1, y: c.y }, { x: x2, y: c.y }]); o.fills = [[fa, fb, { x: x2, y: c.y }, { x: x1, y: c.y }]];
      break;
    }
    case "disjoint": {
      if (!done(2)) break;
      o.lines = [[a, b]];
      if (!done(4)) { if (done(3)) o.lines.push([c, c]); break; }
      o.lines.push([c, e]); o.fills = [[a, b, e, c]];
      break;
    }
    case "regression": {
      if (!done(2)) break;
      const r = regression(f, P[0], P[1]);
      if (!r) { o.lines = [[a, b]]; break; }
      const x1 = f.x(r.i1), x2 = f.x(r.i2), y1 = r.intercept, y2 = r.intercept + r.slope * (r.i2 - r.i1);
      for (const k of [0, 2, -2]) o.lines.push([{ x: x1, y: f.y(y1 + k * r.sigma) }, { x: x2, y: f.y(y2 + k * r.sigma) }]);
      o.fills = [[{ x: x1, y: f.y(y1 + 2 * r.sigma) }, { x: x2, y: f.y(y2 + 2 * r.sigma) }, { x: x2, y: f.y(y2 - 2 * r.sigma) }, { x: x1, y: f.y(y1 - 2 * r.sigma) }]];
      o.labels.push({ x: x2 + 6, y: f.y(y2), text: `Pearson's R ${r.r.toFixed(3)} · σ ${fmt(r.sigma, dec)}` });
      break;
    }
    case "pitchfork": case "schiff": case "mschiff": case "insidefork": case "pitchfan":
      if (!done(2)) break;
      if (!done(3)) { o.lines = [[a, b]]; break; }
      o.lines = pitchfork(f, d.type, a, b, c);
      break;
    case "rect": if (done(2)) { const q = [a, { x: b.x, y: a.y }, b, { x: a.x, y: b.y }]; o.lines = [[...q, a]]; o.fills = [q]; } break;
    case "rotrect": {
      if (!done(2)) break;
      if (!done(3)) { o.lines = [[a, b]]; break; }
      const off = channelOffset(a, b, c), q = [a, b, { x: b.x + off.x, y: b.y + off.y }, { x: a.x + off.x, y: a.y + off.y }];
      o.lines = [[...q, a]]; o.fills = [q];
      break;
    }
    case "circle": if (done(2)) { const q = ellipsePts((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2 || 1, Math.abs(b.y - a.y) / 2 || 1); o.lines = [q]; o.fills = [q]; } break;
    case "triangle": if (done(3)) { o.lines = [[a, b, c, a]]; o.fills = [[a, b, c]]; } else o.lines = seq(pts); break;
    case "arc": if (done(3)) { const q = arcThrough(a, b, c); o.lines = [q]; o.fills = [q]; } else o.lines = seq(pts); break;
    case "curve": o.lines = done(3) ? [bezierQ(a, c, b)] : seq(pts); break;
    case "dcurve": o.lines = done(4) ? [bezierC(a, c, e, b)] : done(3) ? [bezierQ(a, c, b)] : seq(pts); break;
    case "arrow": o.lines = seq(pts); break;
    case "forecast": {
      if (!done(2)) break;
      o.dashed = [[a, b]];
      const bars = Math.round(f.fracIndexAt(b.x) - f.fracIndexAt(a.x));
      o.labels.push({ x: b.x, y: b.y + (b.y < a.y ? -16 : 16), text: `${P[1].price - P[0].price >= 0 ? "+" : ""}${fmt(P[1].price - P[0].price, dec)} (${pct(P[0].price, P[1].price)}) · ${bars} bars · ${formatDuration(P[1].time - P[0].time)}`, align: "center", bg: true });
      break;
    }
    case "projection": if (done(3)) { o.lines = [[a, b], [b, c]]; o.fills = [[a, b, c]]; } else o.lines = seq(pts); break;
    case "daterange": {
      if (!done(2)) break;
      const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x), q = [{ x: x1, y: L.plotTop }, { x: x2, y: L.plotTop }, { x: x2, y: L.priceBottom }, { x: x1, y: L.priceBottom }];
      o.fills = [q]; o.lines = [[q[0], q[3]], [q[1], q[2]]];
      const bars = Math.round(Math.abs(f.fracIndexAt(b.x) - f.fracIndexAt(a.x)));
      o.labels.push({ x: (x1 + x2) / 2, y: Math.min(a.y, b.y) - 12, text: `${bars} bars · ${formatDuration(P[1].time - P[0].time)}`, align: "center", bg: true });
      break;
    }
    case "pricerange": {
      if (!done(2)) break;
      const y1 = Math.min(a.y, b.y), y2 = Math.max(a.y, b.y), q = [{ x: L.plotLeft, y: y1 }, { x: L.plotRight, y: y1 }, { x: L.plotRight, y: y2 }, { x: L.plotLeft, y: y2 }];
      o.fills = [q]; o.lines = [[q[0], q[1]], [q[3], q[2]]];
      o.labels.push({ x: (a.x + b.x) / 2, y: (y1 + y2) / 2, text: `${P[1].price - P[0].price >= 0 ? "+" : ""}${fmt(P[1].price - P[0].price, dec)} (${pct(P[0].price, P[1].price)})`, align: "center", bg: true });
      break;
    }
    case "avwap": {
      const v = anchoredVwap(f, P[0]);
      o.lines = seq(v.map((p) => ({ x: f.x(p.i), y: f.y(p.v) })));
      if (v.length) o.labels.push({ x: L.plotRight + 4, y: f.y(v[v.length - 1].v), text: `AVWAP ${fmt(v[v.length - 1].v, dec)}`, bg: d.style.color, color: "#0B0E14" });
      break;
    }
    case "fib": {
      if (!done(2)) break;
      const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x);
      for (const lv of FIB_LEVELS) { const y = a.y + (b.y - a.y) * lv; o.lines.push([{ x: x1, y }, { x: x2, y }]); o.labels.push({ x: x2 + 4, y, text: `${lv.toFixed(3)}  ${fmt(P[0].price + (P[1].price - P[0].price) * lv, dec)}` }); }
      o.dashed = [[a, b]];
      break;
    }
    case "fibext": {
      if (!done(2)) break;
      o.dashed = [[a, b, ...(done(3) ? [c] : [])]];
      if (!done(3)) break;
      const x2 = Math.max(a.x, b.x, c.x) + 80;
      for (const lv of FIB_EXT_LEVELS) { const price = P[2].price + (P[1].price - P[0].price) * lv, y = f.y(price); o.lines.push([{ x: c.x, y }, { x: x2, y }]); o.labels.push({ x: x2 + 4, y, text: `${lv.toFixed(3)}  ${fmt(price, dec)}` }); }
      break;
    }
    case "fibchannel": {
      if (!done(2)) break;
      o.lines = [[a, b]];
      if (!done(3)) break;
      const off = channelOffset(a, b, c);
      for (const lv of [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.618]) { o.lines.push([{ x: a.x + off.x * lv, y: a.y + off.y * lv }, { x: b.x + off.x * lv, y: b.y + off.y * lv }]); o.labels.push({ x: b.x + off.x * lv + 4, y: b.y + off.y * lv, text: lv.toFixed(3) }); }
      break;
    }
    case "fibtimezone": {
      if (!done(2)) break;
      const dx = b.x - a.x;
      for (const n of FIB_TIME) { const x = a.x + dx * n; if (x < L.plotLeft - 10 || x > L.plotRight + 10) continue; o.lines.push([{ x, y: L.plotTop }, { x, y: L.priceBottom }]); o.labels.push({ x, y: L.priceBottom - 8, text: String(n), align: "center" }); }
      break;
    }
    case "fibtimeext": {
      if (!done(2)) break;
      o.dashed = [[a, b, ...(done(3) ? [c] : [])]];
      if (!done(3)) break;
      const dx = b.x - a.x;
      for (const lv of [0, 0.382, 0.5, 0.618, 1, 1.382, 1.618, 2]) { const x = c.x + dx * lv; o.lines.push([{ x, y: L.plotTop }, { x, y: L.priceBottom }]); o.labels.push({ x, y: L.priceBottom - 8, text: lv.toFixed(3), align: "center" }); }
      break;
    }
    case "fibspeedfan": {
      if (!done(2)) break;
      o.lines = [[a, { x: b.x, y: a.y }], [{ x: b.x, y: a.y }, b], [b, { x: a.x, y: b.y }], [{ x: a.x, y: b.y }, a]];
      for (const lv of [0.25, 0.382, 0.5, 0.618, 0.75]) {
        o.lines.push([a, ext(a, { x: b.x, y: a.y + (b.y - a.y) * lv })], [a, ext(a, { x: a.x + (b.x - a.x) * lv, y: b.y })]);
        o.labels.push({ x: b.x + 4, y: a.y + (b.y - a.y) * lv, text: lv.toFixed(3) }, { x: a.x + (b.x - a.x) * lv, y: b.y + (b.y > a.y ? 10 : -10), text: lv.toFixed(3), align: "center" });
      }
      o.lines.push([a, ext(a, b)]);
      break;
    }
    case "fibcircles": {
      if (!done(2)) break;
      const r = Math.hypot(b.x - a.x, b.y - a.y);
      o.dashed = [[a, b]];
      for (const lv of [0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.618]) { o.lines.push(ellipsePts(a.x, a.y, r * lv, r * lv)); o.labels.push({ x: a.x, y: a.y - r * lv - 8, text: lv.toFixed(3), align: "center" }); }
      break;
    }
    case "fibarcs": {
      if (!done(2)) break;
      const r = Math.hypot(b.x - a.x, b.y - a.y), up = b.y < a.y;
      o.dashed = [[a, b]];
      for (const lv of [0.382, 0.5, 0.618, 1]) { o.lines.push(ellipsePts(b.x, b.y, r * lv, r * lv, up ? 0 : Math.PI, up ? Math.PI : 2 * Math.PI, 40)); o.labels.push({ x: b.x, y: b.y + (up ? r * lv + 10 : -r * lv - 10), text: lv.toFixed(3), align: "center" }); }
      break;
    }
    case "fibspiral": {
      if (!done(2)) break;
      const r0 = Math.hypot(b.x - a.x, b.y - a.y) || 1, t0 = Math.atan2(b.y - a.y, b.x - a.x), phi = 1.618034;
      const q: XY[] = [];
      for (let k = -60; k <= 120; k++) { const t = (k / 30) * Math.PI; const r = r0 * Math.pow(phi, t / (Math.PI / 2)); if (r > FAR) break; q.push({ x: a.x + r * Math.cos(t0 + t), y: a.y + r * Math.sin(t0 + t) }); }
      o.lines = [q]; o.dashed = [[a, b]];
      break;
    }
    case "fibwedge": {
      if (!done(2)) break;
      o.lines = [[a, b]];
      if (!done(3)) break;
      o.lines.push([a, c]);
      const r = Math.hypot(b.x - a.x, b.y - a.y), t1 = Math.atan2(b.y - a.y, b.x - a.x), t2 = Math.atan2(c.y - a.y, c.x - a.x);
      const sweep = ((t2 - t1 + 3 * Math.PI) % (2 * Math.PI)) - Math.PI;
      for (const lv of [0.382, 0.5, 0.618, 1]) { o.lines.push(ellipsePts(a.x, a.y, r * lv, r * lv, t1, t1 + sweep, 24)); o.labels.push({ x: a.x + r * lv * Math.cos(t1 + sweep / 2), y: a.y + r * lv * Math.sin(t1 + sweep / 2), text: lv.toFixed(3), align: "center", size: 9 }); }
      break;
    }
    case "gannbox": case "gannsquare": case "gannfixed": {
      if (!done(2)) break;
      const q = [a, { x: b.x, y: a.y }, b, { x: a.x, y: b.y }];
      o.lines = [[...q, a], [a, b], [q[1], q[3]]]; o.fills = [q];
      const levels = d.type === "gannfixed" ? [0.25, 0.5, 0.75] : GANN;
      for (const lv of levels) {
        const y = a.y + (b.y - a.y) * lv, x = a.x + (b.x - a.x) * lv;
        o.lines.push([{ x: a.x, y }, { x: b.x, y }], [{ x, y: a.y }, { x, y: b.y }]);
        o.labels.push({ x: Math.max(a.x, b.x) + 4, y, text: lv.toFixed(3) }, { x, y: Math.max(a.y, b.y) + 10, text: lv.toFixed(3), align: "center" });
      }
      if (d.type !== "gannbox") for (const [, m] of FAN_RATIOS) o.lines.push([a, ext(a, { x: a.x + (b.x - a.x), y: a.y + (b.y - a.y) * m })]);
      break;
    }
    case "gannfan": {
      if (!done(2)) break;
      const dx = b.x - a.x, dy = b.y - a.y;
      for (const [name, m] of FAN_RATIOS) { const end = ext(a, { x: a.x + dx, y: a.y + dy * m }); o.lines.push([a, end]); const lp = lerp(a, end, Math.min(1, 300 / FAR)); o.labels.push({ x: lp.x, y: lp.y, text: name, size: 9 }); }
      break;
    }
    case "xabcd": case "cypher": {
      o.lines = seq(pts);
      if (done(3)) o.fills.push([a, b, c]);
      if (done(5)) { o.fills.push([c, e, pts[4]]); const r1 = Math.abs(c.y - b.y) / (Math.abs(b.y - a.y) || 1), r2 = Math.abs(pts[4].y - e.y) / (Math.abs(e.y - c.y) || 1); o.labels.push({ x: mid(a, c).x, y: mid(a, c).y, text: r1.toFixed(3), align: "center", size: 10 }, { x: mid(c, pts[4]).x, y: mid(c, pts[4]).y, text: r2.toFixed(3), align: "center", size: 10 }); }
      o.labels.push(...wavePts(pts, ["X", "A", "B", "C", "D"]));
      break;
    }
    case "abcd": o.lines = seq(pts); if (done(4)) o.dashed = [[a, c], [b, e]]; o.labels.push(...wavePts(pts, ["A", "B", "C", "D"])); break;
    case "tripattern": o.lines = seq(pts); if (done(4)) { o.lines = [[a, b, c, e], [a, ext(a, c)], [b, ext(b, e)]]; } o.labels.push(...wavePts(pts, ["1", "2", "3", "4"])); break;
    case "threedrives": o.lines = seq(pts); o.labels.push(...wavePts(pts, ["0", "1", "A", "2", "B", "3", "C"])); break;
    case "headshoulders": {
      o.lines = seq(pts);
      if (done(6)) o.dashed = [[ext(pts[5], b), ext(b, pts[5])]]; // neckline through the two troughs
      o.labels.push(...wavePts(pts, ["", "LS", "", "H", "", "RS", ""]));
      if (done(2)) o.labels[1] = { ...o.labels[1], text: "LS" };
      break;
    }
    case "elliottimpulse": o.lines = seq(pts); o.labels.push(...wavePts(pts, ["0", "1", "2", "3", "4", "5"])); break;
    case "elliottcorrection": o.lines = seq(pts); o.labels.push(...wavePts(pts, ["0", "A", "B", "C"])); break;
    case "elliotttriangle": o.lines = seq(pts); o.labels.push(...wavePts(pts, ["0", "A", "B", "C", "D", "E"])); break;
    case "elliottdouble": o.lines = seq(pts); o.labels.push(...wavePts(pts, ["0", "W", "X", "Y"])); break;
    case "elliotttriple": o.lines = seq(pts); o.labels.push(...wavePts(pts, ["0", "W", "X", "Y", "X", "Z"])); break;
    case "cyclelines": {
      if (!done(2)) { o.lines = [[{ x: a.x, y: L.plotTop }, { x: a.x, y: L.priceBottom }]]; break; }
      const per = Math.abs(b.x - a.x) || 1, x0 = Math.min(a.x, b.x);
      for (let k = -60; k <= 120; k++) { const x = x0 + k * per; if (x < L.plotLeft || x > L.plotRight) continue; o.lines.push([{ x, y: L.plotTop }, { x, y: L.priceBottom }]); }
      break;
    }
    case "timecycles": {
      if (!done(2)) break;
      const per = Math.abs(b.x - a.x) || 1, x0 = Math.min(a.x, b.x);
      for (let k = -60; k <= 120; k++) { const x = x0 + k * per; if (x + per < L.plotLeft || x > L.plotRight) continue; o.lines.push(ellipsePts(x + per / 2, a.y, per / 2, Math.min(per / 2, 60), Math.PI, 2 * Math.PI, 24)); }
      break;
    }
    case "sineline": {
      if (!done(2)) break;
      const per = (b.x - a.x) * 2 || 1, amp = b.y - a.y, q: XY[] = [];
      for (let x = L.plotLeft; x <= L.plotRight; x += 3) q.push({ x, y: a.y + amp * Math.sin(((x - a.x) / per) * 2 * Math.PI) });
      o.lines = [q]; o.dashed = [[a, b]];
      break;
    }
    case "fvgzone": case "obzone": {
      if (!done(2)) break;
      const x1 = Math.min(a.x, b.x), x2 = L.plotRight, y1 = Math.min(a.y, b.y), y2 = Math.max(a.y, b.y);
      const q = [{ x: x1, y: y1 }, { x: x2, y: y1 }, { x: x2, y: y2 }, { x: x1, y: y2 }];
      o.fills = [q]; o.lines = [[q[0], q[1]], [q[3], q[2]]]; o.dashed = [[{ x: Math.max(a.x, b.x), y: y1 }, { x: Math.max(a.x, b.x), y: y2 }]];
      const fill = zoneFill(f, P[0].price, P[1].price, Math.max(idxOf(f, P[0]), idxOf(f, P[1])));
      const bull = P[0].price + P[1].price > 2 * (f.candles[Math.min(idxOf(f, P[0]), idxOf(f, P[1]))]?.close ?? 0) ? "Bearish" : "Bullish";
      o.labels.push({ x: x1 + 6, y: y1 + 9, text: `${d.type === "fvgzone" ? "FVG" : "OB"} · ${bull} · ${fill >= 1 ? "filled" : fill > 0 ? `${Math.round(fill * 100)}% mitigated` : "unmitigated"}`, size: 10 });
      break;
    }
    case "bosmark": case "chochmark": {
      if (!done(2)) break;
      o.lines = [[a, { x: b.x, y: a.y }]]; o.dashed = [[{ x: b.x, y: a.y }, b]];
      const up = b.y < a.y;
      o.labels.push({ x: mid(a, { x: b.x, y: a.y }).x, y: a.y + (up ? 10 : -10), text: `${d.type === "bosmark" ? "BOS" : "CHoCH"} ${up ? "▲" : "▼"} ${fmt(P[0].price, dec)}`, align: "center", bg: d.style.color, color: "#0B0E14", size: 10 });
      break;
    }
    case "liqline": o.dashed = [[a, { x: L.plotRight, y: a.y }]]; o.labels.push({ x: a.x + 4, y: a.y - 9, text: `$$$ Liquidity ${fmt(P[0].price, dec)}`, size: 10 }); break;
    case "sweepmark": {
      const c0 = f.candles[idxOf(f, P[0])]; const up = c0 ? P[0].price >= (c0.high + c0.low) / 2 : true;
      o.lines = [[{ x: a.x, y: a.y + (up ? -22 : 22) }, a], [{ x: a.x - 5, y: a.y + (up ? -7 : 7) }, a, { x: a.x + 5, y: a.y + (up ? -7 : 7) }]];
      o.labels.push({ x: a.x, y: a.y + (up ? -32 : 32), text: "SWEEP", align: "center", size: 10, bg: d.style.color, color: "#0B0E14" });
      break;
    }
    case "eqhl": {
      if (!done(2)) break;
      const y = (a.y + b.y) / 2, c0 = f.candles[idxOf(f, P[0])];
      const high = c0 ? P[0].price >= (c0.high + c0.low) / 2 : true;
      o.dashed = [[{ x: Math.min(a.x, b.x) - 6, y }, { x: Math.max(a.x, b.x) + 6, y }]];
      o.lines = [[{ x: a.x, y: a.y }, { x: a.x, y }], [{ x: b.x, y: b.y }, { x: b.x, y }]];
      o.labels.push({ x: Math.max(a.x, b.x) + 10, y, text: high ? "EQH" : "EQL", bg: d.style.color, color: "#0B0E14", size: 10 });
      break;
    }
    case "swingmark": {
      const c0 = f.candles[idxOf(f, P[0])]; const high = c0 ? P[0].price >= (c0.high + c0.low) / 2 : true;
      o.labels.push({ x: a.x, y: a.y + (high ? -10 : 10), text: high ? "SH" : "SL", align: "center", size: 10, bg: d.style.color, color: "#0B0E14" });
      break;
    }
    case "tickrange": {
      if (!done(2)) break;
      const s = tickStats(f, P[0], P[1]);
      const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x), q = [{ x: x1, y: L.plotTop }, { x: x2, y: L.plotTop }, { x: x2, y: L.priceBottom }, { x: x1, y: L.priceBottom }];
      o.fills = [q]; o.lines = [[q[0], q[3]], [q[1], q[2]]];
      const domSide = s.up >= s.down ? "▲ buyers" : "▼ sellers";
      o.labels.push(
        { x: (x1 + x2) / 2, y: L.plotTop + 12, text: `${s.ticks} ticks · ${s.bars} bars · ${formatDuration(s.seconds)}`, align: "center", size: 10, bg: true },
        { x: (x1 + x2) / 2, y: L.plotTop + 28, text: `Speed ${s.speed.toFixed(2)} t/s · Dominance ${s.dominance.toFixed(0)}% ${domSide} · Imbalance ${s.imbalance >= 0 ? "+" : ""}${s.imbalance.toFixed(0)}%`, align: "center", size: 10, bg: true },
      );
      break;
    }
    case "confluencestamp": {
      const bull = Number(d.meta?.bull ?? 0), bear = Number(d.meta?.bear ?? 0), side = bull >= bear ? "▲" : "▼", score = Math.max(bull, bear);
      o.lines = [[{ x: a.x, y: a.y }, { x: a.x, y: a.y - 18 }]];
      o.labels.push({ x: a.x, y: a.y - 28, text: `BOS ${score} ${side}`, align: "center", size: 11, bg: score >= 80 ? "#00C076" : score >= 60 ? "#F5A623" : "#7C879E", color: "#0B0E14" });
      break;
    }
    case "hlbarrier": {
      o.lines = [[{ x: L.plotLeft, y: a.y }, { x: L.plotRight, y: a.y }]];
      o.fills = [[{ x: a.x, y: L.plotTop }, { x: L.plotRight, y: L.plotTop }, { x: L.plotRight, y: a.y }, { x: a.x, y: a.y }]];
      o.labels.push({ x: L.plotRight + 4, y: a.y, text: fmt(P[0].price, dec), bg: d.style.color, color: "#0B0E14" }, { x: a.x + 6, y: a.y - 10, text: "HIGHER ▲", size: 10, color: "#00C076" }, { x: a.x + 6, y: a.y + 10, text: "LOWER ▼", size: 10, color: "#FF3B30" });
      break;
    }
    case "touchbarrier": {
      const kind = String(d.meta?.kind ?? "touch");
      o.lines = [[{ x: L.plotLeft, y: a.y }, { x: L.plotRight, y: a.y }]];
      o.fills = [[{ x: a.x, y: a.y - 4 }, { x: L.plotRight, y: a.y - 4 }, { x: L.plotRight, y: a.y + 4 }, { x: a.x, y: a.y + 4 }]];
      o.labels.push({ x: L.plotRight + 4, y: a.y, text: fmt(P[0].price, dec), bg: d.style.color, color: "#0B0E14" }, { x: a.x + 6, y: a.y - 10, text: kind === "touch" ? "TOUCH barrier" : "NO TOUCH barrier", size: 10 });
      break;
    }
    case "tickcontract": {
      const n = Math.max(1, Number(d.meta?.ticks ?? 5)), rise = String(d.meta?.direction ?? "rise") === "rise";
      const i0 = f.fracIndexAt(a.x), xe = f.x(i0 + n);
      const top = rise ? L.plotTop : a.y, bottom = rise ? a.y : L.priceBottom;
      o.fills = [[{ x: a.x, y: top }, { x: xe, y: top }, { x: xe, y: bottom }, { x: a.x, y: bottom }]];
      o.lines = [[{ x: a.x, y: L.plotTop }, { x: a.x, y: L.priceBottom }]]; o.dashed = [[{ x: xe, y: L.plotTop }, { x: xe, y: L.priceBottom }], [a, { x: xe, y: a.y }]];
      const exitIdx = Math.round(i0 + n), exitC = f.candles[exitIdx], settled = exitC != null;
      const won = settled ? (rise ? exitC.close > P[0].price : exitC.close < P[0].price) : null;
      o.labels.push({ x: a.x + 4, y: L.plotTop + 10, text: `${rise ? "RISE ▲" : "FALL ▼"} · ${n} tick${n > 1 ? "s" : ""} · entry ${fmt(P[0].price, dec)}`, size: 10, bg: rise ? "#00C076" : "#FF3B30", color: "#0B0E14" });
      o.labels.push({ x: xe + 4, y: L.plotTop + 26, text: settled ? `exit ${fmt(exitC.close, dec)} · ${won ? "WON" : "LOST"}` : "exit pending", size: 10, color: settled ? (won ? "#00C076" : "#FF3B30") : undefined });
      break;
    }
    case "expirymark": o.dashed = [[{ x: a.x, y: L.plotTop }, { x: a.x, y: L.priceBottom }]]; o.labels.push({ x: a.x, y: L.plotTop + 10, text: `EXPIRY ${formatFullTime(P[0].time)}`, align: "center", size: 10, bg: d.style.color, color: "#0B0E14" }); break;
    default: break;
  }
  return o;
}

export const POINT_MARKERS = new Set<DrawingObject["type"]>(["arrowup", "arrowdown", "arrowmarkl", "arrowmarkr", "flag", "emoji", "swingmark", "sweepmark", "confluencestamp", "note", "pricelabel", "signpost"]);
export const CLOSED = new Set<DrawingObject["type"]>(["rect", "rotrect", "circle", "triangle", "arc", "channel", "flatchannel", "disjoint", "regression", "projection", "daterange", "pricerange", "fvgzone", "obzone", "tickrange", "gannbox", "gannsquare", "gannfixed", "xabcd", "cypher"]);
